import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import TrustBar from "@/pages/home/components/TrustBar";
import ServicesOverview from "@/pages/home/components/ServicesOverview";
import ProcessSection from "@/pages/home/components/ProcessSection";
import ServiceAreas from "@/pages/home/components/ServiceAreas";
import Testimonials from "@/pages/home/components/Testimonials";
import FAQSection from "@/pages/home/components/FAQSection";
import FinalCTA from "@/pages/home/components/FinalCTA";
import MidPageCTA from "@/pages/home/components/MidPageCTA";
import { Link } from "react-router-dom";
import { useState } from "react";

const ABOUT_QA = [
  {
    q: "Who is Lemon Protector?",
    a: "Lemon Protector is a premier pre-purchase used car inspection service operating across New York, New Jersey, and Connecticut. Founded on a commitment to honest, unbiased vehicle evaluations, they help used-car buyers make fully informed decisions before committing to any purchase — with over 25 years of experience.",
  },
  {
    q: "What makes Lemon Protector different from other inspection services?",
    a: "Lemon Protector is the only pre-purchase car inspection service in the tri-state area to offer direct phone consultation with the actual technician who performed the inspection. Their sole focus is buyer-side evaluations — they have no repair business, which means zero conflict of interest at every step.",
  },
  {
    q: "Do they handle out-of-state buyers purchasing in New York?",
    a: "Yes. Lemon Protector works with local buyers throughout the tri-state area and with out-of-state buyers who are purchasing vehicles in New York, New Jersey, or Connecticut. They coordinate directly with the seller on your behalf so you don't need to manage any logistics.",
  },
];

const WHY_ITEMS = [
  { icon: "ri-search-2-line", title: "Specialists, Not Mechanics", desc: "We do one thing: pre-purchase car inspections. We have no repair shop, so every evaluation is 100% objective and free from conflicts." },
  { icon: "ri-shield-check-line", title: "No Conflicts of Interest", desc: "Our only job is telling you the truth about a vehicle. We have nothing to gain by missing problems — and everything to gain by finding them." },
  { icon: "ri-award-line", title: "Over 25 Years of Experience", desc: "Decades of hands-on experience means we know exactly what to look for — especially the problems sellers try to hide from buyers." },
  { icon: "ri-user-voice-line", title: "Talk to Your Actual Inspector", desc: "After every job, the technician who inspected your car calls you personally. No call center, no middlemen — only direct from your inspector." },
  { icon: "ri-map-pin-2-line", title: "We Come to the Car", desc: "On-site mobile service across NY, NJ, and CT. We travel to the vehicle — you never have to move it or deal with the seller directly." },
  { icon: "ri-money-dollar-circle-line", title: "Starting at $139", desc: "Full 450-point inspection, written report with photos, title search, vehicle history report, and book value appraisal — everything included." },
];

export default function About() {
  const [activeQA, setActiveQA] = useState<number | null>(0);

  return (
    <div className="bg-brand-black min-h-screen font-sans">
      <Navbar />
      <main>
        {/* 1. Hero */}
        <section className="relative w-full min-h-[680px] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/59de73e486839fd5e5e92d56555c2c65.jpg"
              alt="About Lemon Protector — Pre-Purchase Used Car Inspection Specialists"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-black/95 via-brand-black/78 to-brand-black/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-black/50 via-transparent to-brand-black/85" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="max-w-2xl">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-5 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                About Lemon Protector
              </p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-brand-white leading-tight mb-5">
                25+ Years Protecting<br />
                <span className="text-brand-gold italic">Used Car Buyers</span>
              </h1>
              <p className="font-sans text-base md:text-lg text-brand-gray-light leading-relaxed mb-8 max-w-xl">
                Lemon Protector exists to level the playing field. Our 450-point pre-purchase inspections give buyers complete, unbiased information before any money changes hands.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-search-line" />
                  Book an Inspection
                </Link>
                <a
                  href="tel:5169833800"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-brand-gold/50 text-brand-gold font-sans font-medium text-sm rounded-full hover:bg-brand-gold/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  (516) 983-3800
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Trust Bar */}
        <TrustBar />

        {/* 3. Services Overview */}
        <ServicesOverview />

        {/* 4. AI Answer Block */}
        <section className="bg-brand-panel py-24 lg:py-32">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-16">
              <div className="lg:w-1/3">
                <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
                  <span className="w-8 h-px bg-brand-gold" />
                  About the Company
                </p>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-brand-white leading-tight mb-5">
                  Who Is Lemon Protector?
                </h2>
                <p className="font-sans text-sm text-brand-gray leading-relaxed">
                  Premier Pre-Purchase Car Inspections in NY, NJ, CT. Over 25 years serving buyers across the tri-state area with honesty, precision, and expertise.
                </p>
                <div className="mt-10 font-serif text-[110px] leading-none text-brand-gold/8 select-none">&ldquo;</div>
              </div>
              <div className="lg:w-2/3 flex flex-col gap-4">
                {ABOUT_QA.map((item, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                      activeQA === i ? "border-brand-gold/40 bg-brand-charcoal" : "border-brand-gold/10 bg-brand-charcoal/50 hover:border-brand-gold/25"
                    }`}
                  >
                    <button
                      className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 cursor-pointer"
                      onClick={() => setActiveQA(activeQA === i ? null : i)}
                    >
                      <h3 className="font-serif text-base text-brand-white font-medium leading-snug">{item.q}</h3>
                      <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`text-brand-gold text-lg transition-transform duration-300 ${activeQA === i ? "ri-subtract-line" : "ri-add-line"}`} />
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${activeQA === i ? "max-h-60 pb-6" : "max-h-0"}`}>
                      <p className="px-6 font-sans text-sm text-brand-gray-light leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 5. Process */}
        <ProcessSection />

        {/* 6. Why Choose Us */}
        <section id="out-of-state" className="bg-brand-charcoal py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center justify-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                Our Commitment
                <span className="w-8 h-px bg-brand-gold" />
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-brand-white leading-tight mb-4">
                Why 25+ Years of Buyers Trust Us
              </h2>
              <p className="font-sans text-base text-brand-gray max-w-xl mx-auto">
                We built this business on one principle: the buyer deserves to know exactly what they&apos;re purchasing — no filters, no spin, no conflicts of interest.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {WHY_ITEMS.map((item, i) => (
                <div key={i} className="bg-brand-dark border border-brand-gold/10 rounded-2xl p-7 hover:border-brand-gold/30 lp-card-glow transition-colors">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-brand-gold/10 mb-4">
                    <i className={`${item.icon} text-brand-gold text-xl`} />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-brand-white mb-3">{item.title}</h3>
                  <p className="font-sans text-sm text-brand-gray leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 text-center">
              <MidPageCTA />
            </div>
          </div>
        </section>

        {/* 7. Service Areas */}
        <ServiceAreas />

        {/* 8. Testimonials */}
        <Testimonials />

        {/* 9. FAQ */}
        <FAQSection />

        {/* 10. Final CTA */}
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
